import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FiLock, FiUser, FiEye, FiEyeOff, FiCheck, FiArrowRight } from "react-icons/fi";
import { toast } from "react-toastify";
import logoImg from "../assets/Icons/Logo.png";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.warning("Please fill in both username and password");
      return;
    }

    try {
      setSubmitting(true);
      const user = await login(username, password);
      toast.success(`Welcome back, ${user.name || user.username}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message || "Login failed. Check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0F172A",
        backgroundImage: `
          radial-gradient(at 0% 0%, rgba(6, 78, 59, 0.45) 0px, transparent 50%),
          radial-gradient(at 100% 100%, rgba(16, 185, 129, 0.2) 0px, transparent 50%)
        `,
        padding: "20px",
      }}
    >
      <div
        className="fade-in"
        style={{
          width: "100%",
          maxWidth: 440,
          backgroundColor: "#ffffff",
          borderRadius: "var(--radius-xl)",
          padding: "40px 36px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
        }}
      >
        {/* Brand Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <img
              src={logoImg}
              alt="Commercial IT Logo"
              style={{
                maxHeight: 70,
                maxWidth: 220,
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </div>

          <h1 style={{ fontSize: "1.65rem", fontWeight: 800, color: "var(--navy)", marginBottom: 6 }}>
            Commercial <span style={{ color: "var(--accent)" }}>IT</span>
          </h1>
          <p style={{ fontSize: "0.88rem", color: "var(--slate-500)", fontWeight: 500, margin: 0 }}>
            Inventory Management & Asset Tracking System
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                fontSize: "0.82rem",
                fontWeight: 700,
                color: "var(--slate-700)",
                marginBottom: 6,
              }}
            >
              Username or Email
            </label>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                  display: "flex",
                }}
              >
                <FiUser size={18} />
              </span>
              <input
                id="username-input"
                type="text"
                required
                placeholder="admin or staff@inventory.com"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 14px 12px 42px",
                  borderRadius: "var(--radius-md)",
                  border: "1.5px solid var(--slate-200)",
                  fontSize: "0.92rem",
                  color: "var(--slate-900)",
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--accent)";
                  e.target.style.boxShadow = "0 0 0 3px var(--accent-glow)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "var(--slate-200)";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)" }}>
                Password
              </label>
            </div>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                  display: "flex",
                }}
              >
                <FiLock size={18} />
              </span>
              <input
                id="password-input"
                type={showPassword ? "text" : "password"}
                required
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 42px 12px 42px",
                  borderRadius: "var(--radius-md)",
                  border: "1.5px solid var(--slate-200)",
                  fontSize: "0.92rem",
                  color: "var(--slate-900)",
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--accent)";
                  e.target.style.boxShadow = "0 0 0 3px var(--accent-glow)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "var(--slate-200)";
                  e.target.style.boxShadow = "none";
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--slate-400)",
                  display: "flex",
                }}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <button
            id="login-btn"
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              padding: "13px",
              borderRadius: "var(--radius-md)",
              border: "none",
              background: "linear-gradient(135deg, #064E3B 0%, #059669 100%)",
              color: "#ffffff",
              fontSize: "0.95rem",
              fontWeight: 700,
              cursor: submitting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: "0 4px 14px rgba(6, 78, 59, 0.35)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {submitting ? "Signing In..." : "Sign In to System"}
            {!submitting && <FiArrowRight size={18} />}
          </button>
        </form>


      </div>
    </div>
  );
};

export default Login;
